import { useEffect, useRef, useState } from 'react';
import type { EntraAuthDeviceCodeEvent, EntraAuthEvent } from '../../../types/entraAuth';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { Check, ExternalLink } from 'lucide-react';

type DeviceCodePrompt = EntraAuthDeviceCodeEvent;

async function copyToClipboard(value: string): Promise<boolean> {
  return writeClipboardText(value);
}

export function EntraDeviceCodeDialog() {
  const [prompt, setPrompt] = useState<DeviceCodePrompt | null>(null);
  const promptRef = useRef<DeviceCodePrompt | null>(null);
  const pendingHintTimerRef = useRef<number | null>(null);
  const copyResetTimersRef = useRef<{ url?: number; code?: number }>({});
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  const resetCopiedIndicators = () => {
    setCopiedUrl(false);
    setCopiedCode(false);
    if (copyResetTimersRef.current.url !== undefined) {
      window.clearTimeout(copyResetTimersRef.current.url);
      copyResetTimersRef.current.url = undefined;
    }
    if (copyResetTimersRef.current.code !== undefined) {
      window.clearTimeout(copyResetTimersRef.current.code);
      copyResetTimersRef.current.code = undefined;
    }
  };

  useEffect(() => {
    if (!window.electronAPI?.entraAuth?.onEvent) {
      return;
    }
    const unsubscribe = window.electronAPI.entraAuth.onEvent((event: EntraAuthEvent) => {
      if (event.type === 'deviceCode') {
        if (pendingHintTimerRef.current !== null) {
          window.clearTimeout(pendingHintTimerRef.current);
          pendingHintTimerRef.current = null;
        }
        resetCopiedIndicators();
        promptRef.current = event;
        setPrompt(event);
        pendingHintTimerRef.current = window.setTimeout(() => {
          if (promptRef.current?.connectionId !== event.connectionId) return;
          toast.warning({ title: 'Waiting for Microsoft sign-in…', description: 'If the browser keeps returning to the code entry page, try a different browser/profile or temporarily disable tracking/cookie blockers.', duration: 9000 });
        }, 15000);
        return;
      }

      if (event.type === 'interactiveBrowserPending') {
        toast.default({
          title: 'Check your browser',
          description: 'SQL Studio opened your default browser to sign in with Microsoft Entra ID. Complete the sign-in there and return here.',
          duration: 9000,
        });
        return;
      }

      if (event.type === 'tokenResult') {
        if (pendingHintTimerRef.current !== null) {
          window.clearTimeout(pendingHintTimerRef.current);
          pendingHintTimerRef.current = null;
        }
        const isForActivePrompt = promptRef.current?.connectionId === event.connectionId;
        if (isForActivePrompt) {
          resetCopiedIndicators();
          promptRef.current = null;
          setPrompt(null);
        }

        if (event.success) {
          if (isForActivePrompt) {
            toast.success({ title: 'Signed in', description: 'Returning to SQL Studio…', duration: 3000 });
          }
          return;
        }
        toast.error({ title: 'Entra sign-in failed', description: event.error || 'Token acquisition failed', duration: 7000 });
      }
    });
    return () => {
      if (pendingHintTimerRef.current !== null) {
        window.clearTimeout(pendingHintTimerRef.current);
        pendingHintTimerRef.current = null;
      }
      resetCopiedIndicators();
      unsubscribe();
    };
  }, []);

  const dismissPrompt = () => {
    const connectionId = promptRef.current?.connectionId;
    promptRef.current = null;
    setPrompt(null);
    // Settle the pending sign-in in the main process so the connect attempt
    // fails fast instead of silently waiting out the interactive-auth bound.
    if (connectionId) {
      window.electronAPI?.entraAuth?.abortInteractive?.(connectionId)?.catch(() => {});
    }
  };

  const userCode = prompt?.userCode ?? '';
  const verificationUri = prompt?.verificationUri ?? '';
  const message = prompt?.message ?? '';
  const reason = prompt?.reason?.trim() ?? '';
  const openVerificationUri = () => {
    if (!verificationUri) return;
    window.open(verificationUri, '_blank', 'noopener,noreferrer');
  };

  const handleCopyCodeAndOpen = async () => {
    await handleCopyCode();
    openVerificationUri();
  };

  const handleCopyUrl = async () => {
    if (!verificationUri) return;
    const success = await copyToClipboard(verificationUri);
    if (!success) {
      toast.error({ title: 'Copy failed', description: 'Unable to copy URL to clipboard.' });
      return;
    }
    setCopiedUrl(true);
    if (copyResetTimersRef.current.url !== undefined) {
      window.clearTimeout(copyResetTimersRef.current.url);
    }
    copyResetTimersRef.current.url = window.setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleCopyCode = async () => {
    if (!userCode) return;
    const success = await copyToClipboard(userCode);
    if (!success) {
      toast.error({ title: 'Copy failed', description: 'Unable to copy code to clipboard.' });
      return;
    }
    setCopiedCode(true);
    if (copyResetTimersRef.current.code !== undefined) {
      window.clearTimeout(copyResetTimersRef.current.code);
    }
    copyResetTimersRef.current.code = window.setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <Dialog open={!!prompt} onOpenChange={(open) => (!open ? dismissPrompt() : undefined)}>
      <DialogContent className="sm:max-w-[560px]" data-testid="entra-device-code-dialog">
        <DialogHeader>
          <DialogTitle>Microsoft Entra sign-in</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {reason ? (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              <div className="text-xs font-medium text-amber-700 dark:text-amber-300">
                Why am I seeing this?
              </div>
              <div className="mt-1 text-amber-800 dark:text-amber-200 whitespace-pre-wrap">
                {reason}
              </div>
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{message}</p>
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground">Verification URL</div>
            <div className="mt-1 break-all font-mono text-sm">{verificationUri}</div>
          </div>
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="text-xs text-muted-foreground">User code</div>
            <div className="mt-1 font-mono text-xl tracking-widest">{userCode}</div>
          </div>
        </div>
        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleCopyCodeAndOpen()}
              disabled={!verificationUri || !userCode}
              data-testid="entra-copy-code-open-url"
            >
              {copiedCode ? <Check /> : <ExternalLink />}
              {copiedCode ? 'Copied + opened' : 'Copy code + open URL'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleCopyUrl()}
              disabled={!verificationUri}
              data-testid="entra-copy-url"
            >
              {copiedUrl ? (<><Check />Copied</>) : 'Copy URL'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleCopyCode()}
              disabled={!userCode}
              data-testid="entra-copy-code"
            >
              {copiedCode ? (<><Check />Copied</>) : 'Copy code'}
            </Button>
          </div>
          <Button type="button" onClick={dismissPrompt} data-testid="entra-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
